import React, {useMemo} from 'react';
import {useIntl} from 'react-intl';
import {ComboBox, FlexRow, Text} from '@forecast-it/design-system';
import {NovaTestTask} from '../shared/novaTaskVariables';

interface TaskOption {
	// react-aria keys its collection off `id`, and hands this value back through `onChange`.
	id: string;
	label: string;
	taskNumber: string | null;
}

// A combobox with an empty collection closes its popover, which reads as a stall rather than as an
// answer. Rather than leave the author guessing, an empty result is shown as one unselectable row
// that says which kind of empty it is. The id is only ever a disabled key, so it can never reach
// `onSelectTask`.
const EMPTY_OPTION_ID = 'nova-task-picker-empty';

interface Props {
	tasks: NovaTestTask[];
	isLoading: boolean;
	selectedTask: NovaTestTask | null;
	// The text in the box. Owned by the caller because it drives a server-side query rather than a
	// local filter, so the caller is what decides when to ask for a narrower list.
	searchText: string;
	onSearchTextChange: (searchText: string) => void;
	// The caller MUST reflect the chosen task in `searchText`. This box controls both its input value
	// and its selected key, and useComboBoxState skips its own input-reset in that case, leaving the
	// displayed text to the application. A caller that only records the task gets a field still
	// showing the old text, which reads as the selection not taking.
	onSelectTask: (task: NovaTestTask) => void;
	onClearTask: () => void;
	dataCy: string;
}

// Picks the one task the dry run will read, from anywhere in the company.
//
// The list is not filtered here. `tasks` is already the answer to the current search text — the
// database does the matching (task name, or the T-number when the text looks like one) so a task
// outside whatever page happens to be loaded is still findable, which a client-side filter over a
// fetched window could never manage. Passing `items` rather than `defaultItems` is what tells the
// combobox not to filter a second time on top of that.
export function NovaTaskPicker({
	tasks,
	isLoading,
	selectedTask,
	searchText,
	onSearchTextChange,
	onSelectTask,
	onClearTask,
	dataCy,
}: Props): JSX.Element {
	const {formatMessage} = useIntl();

	const hasSearchText = searchText.trim() !== '';
	const emptyMessageId = hasSearchText ? 'nova.test.pickerEmpty' : 'nova.test.pickerNoTasks';
	const isEmpty = !isLoading && tasks.length === 0;

	const options: TaskOption[] = useMemo(() => {
		if (isEmpty) {
			return [{id: EMPTY_OPTION_ID, label: formatMessage({id: emptyMessageId}), taskNumber: null}];
		}
		return tasks.map(task => ({
			id: task.taskId,
			label: task.name,
			taskNumber: task.companyTaskId === null ? null : `T${task.companyTaskId}`,
		}));
	}, [tasks, isEmpty, emptyMessageId, formatMessage]);

	// react-aria hands back `null` when it clears its own selection — typing over a chosen task does
	// exactly that — so a null key has to clear the caller's selection rather than be dropped, or the
	// box would show one thing while the run used another.
	const handleChange = (taskId: string | null): void => {
		if (taskId === null) {
			onClearTask();
			return;
		}
		const chosen = tasks.find(task => task.taskId === taskId);
		if (chosen !== undefined) {
			onSelectTask(chosen);
		}
	};

	const placeholderId = isLoading ? 'nova.test.pickerLoading' : 'nova.test.pickerPlaceholder';

	return (
		<ComboBox
			data-cy={dataCy}
			label={formatMessage({id: 'nova.test.pickerLabel'})}
			placeholder={formatMessage({id: placeholderId})}
			items={options}
			inputValue={searchText}
			onInputChange={onSearchTextChange}
			onChange={handleChange}
			// `value`, not `selectedKey`: ComboBox destructures `value` out of its props and then assigns
			// `ariaProps.selectedKey = value` unconditionally, so anything passed as `selectedKey` is
			// overwritten with undefined and the box is left uncontrolled.
			value={selectedTask === null ? null : selectedTask.taskId}
			// Opening on focus means the prefetched list is visible without having to type first.
			menuTrigger="focus"
			disabledKeys={isEmpty ? [EMPTY_OPTION_ID] : undefined}
			width={ComboBox.WIDTH.FLEX}
		>
			{(option: TaskOption) => (
				<ComboBox.Item textValue={option.label}>
					<FlexRow gap="s">
						{option.taskNumber === null ? null : <Text color="medium">{option.taskNumber}</Text>}
						<Text noWrap>{option.label}</Text>
					</FlexRow>
				</ComboBox.Item>
			)}
		</ComboBox>
	);
}
